import React from 'react';

// Utility: Simple CSV parser (assumes no quoted commas)
function parseCSV(text) {
  const lines = text.trim().split('\n');
  const headers = lines[0].split(',').map(h => h.trim());
  return lines.slice(1).map(line => {
    // Handle quoted fields with commas
    const values = [];
    let cur = '', inQuotes = false;
    for (let i = 0; i < line.length; ++i) {
      const c = line[i];
      if (c === '"') {
        inQuotes = !inQuotes;
      } else if (c === ',' && !inQuotes) {
        values.push(cur);
        cur = '';
      } else {
        cur += c;
      }
    }
    values.push(cur);
    const obj = {};
    headers.forEach((h, i) => obj[h] = values[i] ? values[i].trim() : '');
    return obj;
  });
}

function formatNumber(number) {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number(number));
}


export async function calculate(answers, opts = {}) {
  const panels = Number(answers.panels);
  const grant = answers.grant ? answers.grant.toString() : "2025";
  const battery = answers.battery === "Yes" ? "Yes" : "No";
  let items = [];

  // Require CSV URL via opts.data or answers.data, no default fallback
  const csvUrl = opts.data || answers.data;
  if (!csvUrl) {
    return {
      status: "Error",
      items: [],
      message: "No CSV data URL provided.",
      color: "red",
      title: "Calculation Error"
    };
  }
  let data;
  try {
    const resp = await fetch(csvUrl);
    const csvText = await resp.text();
    data = parseCSV(csvText);
  } catch (e) {
    return {
      status: "Error",
      items: [],
      message: "Could not load solar system data.",
      color: "red",
      title: "Calculation Error"
    };
  }

  // Find system row for selected panels
  const system = data.find(row => parseInt(row["Panels"]) === panels);
  const messages = data.find(row => row["Panels"] === "Message");
  if (!system || !messages) {
    return {
      status: "Error",
      items: [],
      message: "No data available for the selected panel count.",
      color: "red",
      title: "Calculation Error"
    };
  }

  // Parse costs
  let [baseLower, baseUpper] = system["Cost"].split(",").map(s => parseFloat(s.replace("€", "").replace(/\s/g, '')));
  let [batteryLower, batteryUpper] = system["Battery Cost"].split(",").map(s => parseFloat(s.replace("€", "").replace(/\s/g, '')));
  let lowerCost = baseLower;
  let upperCost = baseUpper;
  let annualSavingsValue = parseFloat(system["Annual Savings (No Battery)"].replace("€", "").replace(",", "").replace(/\s/g, ''));
  let lifetimeSavingsValue = parseFloat(system["Lifetime Savings (No Battery)"].replace("€", "").replace(",", "").replace(/\s/g, ''));
  let paybackPeriodValue = getPaybackPeriod(system, grant, battery);
  let batteryMessage = messages["Battery Cost"];
  let batteryTooltip = messages["Annual Savings (No Battery)"];

  if (battery === "Yes") {
    lowerCost += batteryLower;
    upperCost += batteryUpper;
    annualSavingsValue = parseFloat(system["Annual Savings (Battery)"].replace('€', '').replace(',', '').replace(/\s/g, ''));
    lifetimeSavingsValue = parseFloat(system["Lifetime Savings (Battery)"].replace('€', '').replace(',', '').replace(/\s/g, ''));
    batteryTooltip = messages["Annual Savings (Battery)"];
  }

  // Subtract grant
  lowerCost -= parseFloat(system[grant].replace("€", "").replace(/\s/g, ''));
  upperCost -= parseFloat(system[grant].replace("€", "").replace(/\s/g, ''));

  // Ensure lowerCost is the minimum and upperCost is the maximum
  if (lowerCost > upperCost) {
    [lowerCost, upperCost] = [upperCost, lowerCost];
  }

  // Format outputs
  const estimatedCost = `€${formatNumber(lowerCost)} - €${formatNumber(upperCost)}`;
  const annualSavings = `€${formatNumber(annualSavingsValue)}`;
  const lifetimeSavings = `€${formatNumber(lifetimeSavingsValue)}`;
  const paybackPeriod = paybackPeriodValue;
  const powerOutput = system["Yearly Power Output"];
  const panelMessage = system["System Description"];
  const grantMessage = messages[grant];

  // Items for display
  items.push(<span>{panelMessage}</span>);
  items.push(<span>{grantMessage}</span>);
  if (battery === "Yes") {
    items.push(<span>{batteryMessage}</span>);
  }

  // Table for detailed breakdown
  const table = [
    {
      name: "Estimated Installation Cost",
      value: estimatedCost,
      explanation: `This figure includes the SEAI grant reduction of ${grantMessage}.`
    },
    {
      name: "Annual Electricity Savings",
      value: annualSavings,
      explanation: batteryTooltip
    },
    {
      name: "Lifetime Electricity Savings",
      value: lifetimeSavings,
      explanation: messages["Lifetime Savings (Battery)"]
    },
    {
      name: "Payback Period",
      value: paybackPeriod,
      explanation: messages["Payback Period (NB & 2026)"]
    },
    {
      name: "Yearly Power Output",
      value: powerOutput,
      explanation: "Estimated annual energy production from your solar system."
    }
  ];

  return {
    status: estimatedCost,
    items,
    title: "Estimated Installation Cost",
    table
  };
}

// Helper for payback period (mirrors old widget logic)
function getPaybackPeriod(system, grant, battery) {
  const grantString = grant.toString();
  if (grantString === '2024') {
    return battery === 'Yes' ? system['Payback Period (NB & 2024)'] : system['Payback Period (B & 2024)'];
  }
  if (grantString === '2025') {
    return battery === 'Yes' ? system['Payback Period (NB & 2025)'] : system['Payback Period (B & 2025)'];
  }
  if (grantString === '2026') {
    return battery === 'Yes' ? system['Payback Period (NB & 2026)'] : system['Payback Period (B & 2026)'];
  }
  return "";
}
